// @ts-check

import { LOCAL_STORAGE_KEY_REFRESH_TOKEN } from '@/core/constants'
import { i18n } from '@/i18n'
import { store } from '@/store'
import axios from 'axios'

const OAUTH_BASE_URL = process.env.VUE_APP_OAUTH_API_URL || ''
const CLIENT_ID = process.env.VUE_APP_OAUTH_CLIENT_ID || ''
const CLIENT_SECRET = process.env.VUE_APP_OAUTH_CLIENT_SECRET || ''

/**
 * Makes authentication request for the given authentication credentials.
 * @param {string} username
 * @param {string} password
 * @returns {Promise<import('axios').AxiosResponse<BearerTokenResource>>}
 */
export const getToken = async (username, password) =>
  axios.post(`${OAUTH_BASE_URL}/token`, {
    username,
    password,
    grant_type: 'password',
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    scope: '*'
  })

/**
 * Returns personal access token.
 * @param {string} name
 * @param {string} accessToken
 * @returns {Promise<import('axios').AxiosResponse<PersonalAccessTokenResource>>}
 */
export const getPersonalAccessToken = async (name, accessToken) =>
  axios.post(
    `${OAUTH_BASE_URL}/personal-access-tokens`,
    {
      name
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  )

/**
 * @param {string} code
 * @returns {Promise<BearerTokenResource>}
 */
export const exchangeUserCode = async (code) => {
  const res = await axios.post(`${OAUTH_BASE_URL}/auth/user/exchange/${code}`)
  return res.data
}

/**
 * Deletes personal access token.
 * @param {string} personalAccessTokenId
 * @param {string} accessToken
 */
export const deletePersonalAccessToken = async (
  personalAccessTokenId,
  accessToken
) =>
  axios.delete(
    `${OAUTH_BASE_URL}/personal-access-tokens/${personalAccessTokenId}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  )

// Allow only one update token request at a time
/** @type {?Promise<import('axios').AxiosResponse>} */
let updateTokenRequest = null

export const updateToken = () => {
  const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEY_REFRESH_TOKEN)

  if (!updateTokenRequest) {
    if (!refreshToken || refreshToken.length === 0) {
      store.dispatch('auth/logout', {
        message: store.getters['auth/isLoggedIn']
          ? i18n.global.t('login.system_logout')
          : null
      })
      updateTokenRequest = null
      return Promise.reject(new Error('Logged out while updating token!'))
    }

    updateTokenRequest = axios
      .post(`${OAUTH_BASE_URL}/token`, {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        scope: '*',
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET
      })
      .then((response) => {
        const { data } = response
        store.commit('auth/setAccessToken', data.access_token)
        store.commit('auth/setRefreshToken', data.refresh_token)

        const expirationTime = new Date()
        expirationTime.setSeconds(expirationTime.getSeconds() + data.expires_in)
        localStorage.setItem(
          'ukkopro-session-expiration-time',
          expirationTime.toString()
        )

        updateTokenRequest = null

        return Promise.resolve(response)
      })
      .catch((error) => {
        store.dispatch('auth/logout')
        updateTokenRequest = null

        return Promise.reject(error)
      })
  }

  return updateTokenRequest
}
